import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  ErrorHandler,
  NgModule,
  inject,
  provideAppInitializer,
} from "@angular/core";
import { MatDialogModule } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  MatSnackBarModule,
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
} from "@angular/material/snack-bar";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CookieService } from "ngx-cookie-service";
import { NgxSpinnerModule } from "ngx-spinner";
import { Router } from "@angular/router";

// Providers
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideGranumTheme } from "@granumcl/themes";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthGuard } from "./core/services/auth.guard";
import { GoogleTagService } from "./core/services/google-tag.service";
import { RequestInterceptor } from "./core/services/request.interceptor";
import { DataAnalyticsService } from "./core/services/data-analytics.service";
import { DataDogService } from "./core/services/data-dog.service";
import {
  ChunkErrorHandlerService,
  SESSION_KEY_COUNT,
  SESSION_KEY_DESTINATION,
} from "./services/chunk-error-handler.service";

function GoogleTagFactory(provider: GoogleTagService): () => void {
  return () => {
    provider.loadGoogleTag();
  };
}

function DataAnalyticsFactory(provider: DataAnalyticsService): () => void {
  return () => {
    provider.loadAnalytics();
  };
}

function DataDogServiceFactory(provider: DataDogService): () => void {
  return () => {
    provider.initDataDog();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    NgxSpinnerModule,
  ],
  providers: [
    { provide: ErrorHandler, useClass: ChunkErrorHandlerService },
    AuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 3000,
        horizontalPosition: "right",
        verticalPosition: "top",
      },
    },
    provideAppInitializer(() => {
      GoogleTagFactory(inject(GoogleTagService))();
      DataAnalyticsFactory(inject(DataAnalyticsService))();
      DataDogServiceFactory(inject(DataDogService))();
    }),
    CookieService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideGranumTheme(),
    provideAppInitializer(async () => {
      try {
        const destination = sessionStorage.getItem(SESSION_KEY_DESTINATION);
        sessionStorage.removeItem(SESSION_KEY_DESTINATION);
        sessionStorage.removeItem(SESSION_KEY_COUNT);
        if (!destination) {
          return;
        }
        const router = inject(Router);
        await router.navigateByUrl(destination);
      } catch {
        // Recovery navigation failed; fall through to default startup
      }
    }),
  ],
})
export class AppModule {}
